/**
 * The Outlook panel (AIC-158; idc-portal-design v1.1), on Today and Admin → Settings (and, as its own card built on the same
 * actions, My profile, AIC-815): checking, connected, reconnect
 * needed, not connected (plus not set up / not mapped / couldn't check). Connecting asks tracker-api for a one-time
 * URL (an authenticated POST, so the bearer token never goes in a URL) and navigates to it; Microsoft sends the browser
 * back to Today with ?outlook=<outcome>. Connecting only lets the portal create drafts in your own mailbox later:
 * nothing is ever sent, and in build mode every draft is addressed to the build tester. Test connection reports
 * through one promise toast; Disconnect goes through a confirm dialog.
 */
import { useEffect, useRef, useState } from "react";
import { ApiError } from "../api/client.ts";
import { describeError, isPreviewRefusal, previewRefusal, useAction, useLoad, usePortal, type Load } from "../api/hooks.ts";
import type { GraphCheck, GraphStatus } from "../api/schemas.ts";
import { ConfirmDialog } from "./ConfirmDialog.tsx";
import { Icon } from "./Sprites.tsx";
import { Skeleton, StateMessage } from "./states.tsx";
import { useToast } from "./Toast.tsx";

const OUTCOME: Record<string, { tone: "ok" | "info" | "warn"; text: string }> = {
  connected: { tone: "ok", text: "Outlook is connected." },
  denied: { tone: "info", text: "Outlook wasn't connected: the Microsoft sign-in was cancelled or declined." },
  expired: { tone: "info", text: "That connect link expired or was already used. Choose Connect Outlook again." },
  mismatch: { tone: "warn", text: "Outlook wasn't connected: sign in to Microsoft with the same IDC account you use in this portal, in this browser." },
  permission: { tone: "warn", text: "Outlook permission missing: Microsoft refused to read your profile. Ask an admin to grant the portal app the delegated User.Read permission, then connect again." },
  error: { tone: "warn", text: "Outlook couldn't be connected. Try again in a minute." },
};
const NOTICE_CLASS = { ok: "ipd-notice ipd-notice--calm", info: "ipd-notice ipd-notice--calm", warn: "ipd-notice ipd-notice--warn" } as const;

/** The notice for a ?outlook= value the callback redirected with; null for anything else. */
export function OutlookOutcome({ outcome }: { outcome: string | null }) {
  const o = outcome ? OUTCOME[outcome] : undefined;
  if (!o) return null;
  return <div className={`${NOTICE_CLASS[o.tone]} ipd-ol-outcome`} role={o.tone === "warn" ? "alert" : "status"}><span>{o.text}</span></div>;
}

/** Why a Test connection failed, in words (tracker-api graph/connections.ts graphFailureReason and check). */
const CHECK_REASON: Readonly<Record<string, string>> = {
  token_error: "Microsoft didn't return a sign-in token.",
  graph_timeout: "Microsoft took too long to answer.",
  graph_network: "Microsoft couldn't be reached.",
  graph_unauthorized: "Microsoft no longer accepts this sign-in.",
  graph_forbidden: "Microsoft refused access.",
  graph_throttled: "Microsoft is limiting requests right now.",
  graph_unavailable: "Microsoft isn't available right now.",
};

/** What a failed Test connection tells the analyst (AIC-164): specific for Graph's 403 and 401, generic otherwise. */
export function checkFailureText(c: Extract<GraphCheck, { ok: false }>): string {
  if (c.status === 403) {
    return c.code === "Authorization_RequestDenied"
      ? "Outlook permission missing: ask an admin to grant the portal app the delegated User.Read permission, then test again."
      : `Microsoft refused access (${c.code ?? "forbidden"}). Ask an admin to check the portal app's Outlook permissions.`;
  }
  if (c.status === 401 || c.reason === "reconnect_needed") return "Microsoft no longer accepts this Outlook sign-in. Reconnect Outlook.";
  if (c.reason === "not_connected") return "Outlook isn't connected. Connect Outlook first.";
  return `${CHECK_REASON[c.reason] ?? "The connection didn't work."} Connect Outlook again.`;
}

export type OutlookState = "checking" | "connected" | "reconnect" | "off" | "unavailable" | "unmapped" | "preview" | "error";

/** Which panel state a graph-status load is in. */
export function outlookState(load: Load<GraphStatus>): OutlookState {
  if (load.state === "loading") return "checking";
  // AIC-254: an admin's analyst preview never sees the analyst's Outlook connection (/me/graph is outside its allowlist).
  if (load.state === "error") return isPreviewRefusal(load.error) ? "preview" : "error";
  const s = load.data;
  if (!s.available) return "unavailable";
  if (!s.mapped) return "unmapped";
  if (!s.connected) return "off";
  return s.reconnect_needed ? "reconnect" : "connected";
}

/**
 * AIC-479: bookings are on but this connection was made without the calendar permission, so vendors can't book a call
 * with this analyst until they connect again.
 */
export function bookingsNeedReconnect(s: GraphStatus | null): boolean {
  return Boolean(s && s.connected && !s.reconnect_needed && s.bookings?.enabled && s.calendar === false);
}

/** Drafts can be created right now. */
export const outlookReady = (state: OutlookState) => state === "connected";

export function useGraphStatus() {
  const { preview = false } = usePortal();
  return useLoad((api) => (preview ? previewRefusal() : api.graphStatus()), [preview]);
}

const shortDate = (iso: string | null | undefined) => {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const BADGE: Readonly<Record<Exclude<OutlookState, "checking" | "error">, { tone: string; text: string }>> = {
  connected: { tone: "done", text: "Connected" },
  reconnect: { tone: "you", text: "Reconnect needed" },
  off: { tone: "off", text: "Not connected" },
  unavailable: { tone: "off", text: "Not set up" },
  unmapped: { tone: "off", text: "Not connected" },
  preview: { tone: "off", text: "Not available in preview" },
};
const DATA_OL: Readonly<Record<OutlookState, string>> = {
  checking: "checking", connected: "connected", reconnect: "reconnect", off: "off", unavailable: "unavailable", unmapped: "unavailable", preview: "preview", error: "error",
};

/**
 * The Outlook actions a panel offers (connect, test, disconnect), shared by this panel and the My profile page's own
 * Outlook card (AIC-815): one set of toasts and refusals. `connectRef` is focused after a disconnect, when Connect Outlook
 * is what the panel then offers.
 */
export function useOutlookActions(load: Load<GraphStatus> & { reload: () => void }, go: (url: string) => void = (url) => window.location.assign(url)) {
  const { api, expire } = usePortal();
  const act = useAction();
  const toast = useToast();
  const [confirm, setConfirm] = useState(false);
  const [focusConnect, setFocusConnect] = useState(false);
  const [testing, setTesting] = useState(false);
  const connectRef = useRef<HTMLButtonElement>(null);
  const state = outlookState(load);

  useEffect(() => {
    if (focusConnect && state === "off") {
      connectRef.current?.focus();
      setFocusConnect(false);
    }
  }, [focusConnect, state]);

  const connect = async () => {
    if (act.busy) return;
    const url = await act.run((api) => api.graphConnect());
    if (url) go(url);
    else toast.show({ tone: "err", title: "Outlook couldn't be connected", desc: "The connect link didn't come back. Nothing changed; try again.", action: { label: "Try again", onAction: () => void connect() } });
  };
  const test = async () => {
    if (testing) return;
    setTesting(true);
    const t = toast.show({ tone: "busy", title: "Testing your Outlook connection…" });
    let c: GraphCheck;
    try {
      c = await api.graphCheck();
    } catch (e) {
      setTesting(false);
      if (e instanceof ApiError && e.status === 401) expire();
      const err = e instanceof Error ? e : new Error(String(e));
      t.update({ tone: "err", title: "Outlook didn't answer", desc: `${describeError(err)} Drafts wait and retry; nothing is lost.`, action: { label: "Test again", onAction: () => void test() } });
      return;
    }
    setTesting(false);
    if (c.ok) {
      t.update({ tone: "ok", title: "Outlook connection OK", desc: c.upn ? `Outlook answered for ${c.upn}.` : "Outlook answered." });
    } else {
      t.update({ tone: "err", title: "The Outlook connection didn't work", desc: checkFailureText(c), action: { label: "Test again", onAction: () => void test() } });
    }
  };
  const disconnect = async () => {
    setConfirm(false);
    const done = await act.run(async (api) => { await api.graphDisconnect(); return true; });
    if (done) {
      toast.show({ tone: "ok", title: "Outlook disconnected", desc: "No new drafts are created until you connect again. Drafts already in Outlook stay where they are." });
      setFocusConnect(true);
      load.reload();
    } else {
      toast.show({ tone: "err", title: "Outlook wasn't disconnected", desc: "Nothing changed. Try again in a minute.", action: { label: "Try again", onAction: () => setConfirm(true) } });
    }
  };
  return { act, testing, confirm, setConfirm, connect, test, disconnect, connectRef };
}

/**
 * The panel for a graph-status load owned by the caller (Today also uses it for the hero chip and Create draft).
 * blocked names what is stuck while Outlook needs attention, e.g. "Example Co's invite draft".
 */
export function OutlookPanel({ load, blocked, go = (url: string) => window.location.assign(url) }: {
  load: Load<GraphStatus> & { reload: () => void }; blocked?: string | null; go?: (url: string) => void;
}) {
  const { buildMode = true } = usePortal();
  const { act, testing, confirm, setConfirm, connect, test, disconnect, connectRef } = useOutlookActions(load, go);
  const state = outlookState(load);

  const s = load.state === "ready" ? load.data : null;
  const since = shortDate(s?.connected_at);
  const disconnectButton = <button type="button" className="ipd-btn" onClick={() => setConfirm(true)}>Disconnect</button>;
  let body;
  if (state === "checking") {
    body = (
      <>
        <p className="ipd-sr">Checking your Outlook connection…</p>
        <div aria-hidden="true" className="ipd-ol-sk"><Skeleton width="92%" /><Skeleton width="70%" /></div>
      </>
    );
  } else if (state === "error" && load.state === "error") {
    body = (
      <StateMessage tone="risk" size="sm" title="Couldn't check your Outlook connection." actions={<button type="button" className="ipd-btn" onClick={load.reload}><Icon name="retry" />Try again</button>}>
        {describeError(load.error)} Nothing changed; drafts wait until the check works.
      </StateMessage>
    );
  } else if (state === "unavailable") {
    body = <p>Connecting Outlook isn't set up on this server yet.</p>;
  } else if (state === "preview") {
    body = <p>Not available in preview. The analyst preview never opens the analyst&rsquo;s Outlook connection or mailbox.</p>;
  } else if (state === "unmapped") {
    body = <p>You can connect once an admin maps your account to a Software Tracker analyst.</p>;
  } else if (state === "off") {
    body = (
      <>
        <p>Connect so invite and reminder drafts can be created in your Drafts folder. Software Tracker never sends email for you.</p>
        <div className="ipd-ol-actions">
          <button ref={connectRef} type="button" className="ipd-btn ipd-btn--go" aria-busy={act.busy || undefined} aria-disabled={act.busy || undefined} onClick={() => void connect()}>Connect Outlook</button>
        </div>
      </>
    );
  } else if (state === "reconnect") {
    body = (
      <>
        <p>
          Microsoft no longer accepts your saved sign-in{s?.upn ? <> for <b>{s.upn}</b></> : null}, so drafts can't be created until you reconnect.
          {blocked ? <> <b>{blocked}</b> — waiting for you to reconnect Outlook.</> : null}
        </p>
        <div className="ipd-ol-actions">
          <button type="button" className="ipd-btn ipd-btn--go" aria-busy={act.busy || undefined} aria-disabled={act.busy || undefined} onClick={() => void connect()}>Reconnect Outlook</button>
          {disconnectButton}
        </div>
      </>
    );
  } else {
    body = (
      <>
        <p>
          {s?.upn ? <>as <b>{s.upn}</b></> : "Connected"}{since ? <> · since {since}</> : null}. Invite and reminder drafts appear in your Drafts folder. You send them.
        </p>
        {bookingsNeedReconnect(s) ? (
          <p className="ipd-ol-bookings" data-testid="outlook-bookings">
            <b>Reconnect Outlook to enable bookings.</b> Vendors can book a call with you once Outlook may add calendar holds (holds invite no one; you send the invitation).
            Until then they can only send you a message.
          </p>
        ) : null}
        <div className="ipd-ol-actions">
          {bookingsNeedReconnect(s) ? <button type="button" className="ipd-btn ipd-btn--go" aria-busy={act.busy || undefined} aria-disabled={act.busy || undefined} onClick={() => void connect()}>Reconnect Outlook</button> : null}
          <button type="button" className="ipd-btn" aria-busy={testing || undefined} aria-disabled={testing || undefined} onClick={() => void test()}>{testing ? "Testing…" : "Test connection"}</button>
          {disconnectButton}
        </div>
      </>
    );
  }
  const badge = state === "checking" || state === "error" ? null : BADGE[state];
  return (
    <section className="ipd-card ipd-outlook" id="outlook" data-ol={DATA_OL[state]} tabIndex={-1} aria-labelledby="outlook-h" aria-busy={state === "checking" || undefined}>
      <div className="ipd-ol">
        <div className="ipd-ol-top">
          <h2 id="outlook-h"><Icon name="mail" />Outlook</h2>
          {badge ? <span className={`ipd-badge ipd-t--${badge.tone}`}>{badge.text}</span> : null}
          {state === "checking" ? <span aria-hidden="true"><Skeleton variant="pill" style={{ width: 92, height: 20 }} /></span> : null}
        </div>
        <div className="ipd-ol-body" key={state}>{body}</div>
        {buildMode && state !== "checking" && state !== "preview" ? <p className="ipd-ol-build">Build mode: drafts are addressed to the build tester, not the vendor.</p> : null}
      </div>
      <ConfirmDialog
        open={confirm}
        title="Disconnect Outlook?"
        body="Software Tracker will stop creating invite and reminder drafts in your Drafts folder until you reconnect. Drafts already in Outlook stay where they are."
        cancelLabel="Keep connected"
        confirmLabel="Disconnect"
        onCancel={() => setConfirm(false)}
        onConfirm={() => void disconnect()}
      />
    </section>
  );
}

/** Self-loading panel (Admin → Settings). */
export function OutlookConnection({ go }: { go?: (url: string) => void }) {
  const load = useGraphStatus();
  return <OutlookPanel load={load} go={go} />;
}
